import { reviewComplete } from './sections.ts';
import type { TokenSchema } from './tokenSchema.ts';

/**
 * The wizard spine — the ordered steps + the landing-step logic — as PURE data/logic
 * (no React, no supabase), so the gating/landing rules are unit-testable (the component
 * imports these). IA-2 reorder: Site structure + SEO move BEFORE Build; reorder the array,
 * keep the `key`s (`review`/`send_live` are referenced by build history + persisted state).
 */

export type StepKind = 'working' | 'coming';
export interface StepDef {
  key: string;
  label: string; // short rail label
  plain: string; // plain-language one-liner
  kind: StepKind;
  // Optional working steps are real, but never gate the steps after them — a designer can
  // proceed without them (degrade-gracefully). SEO/Content & media are optional: useful,
  // but they must never hold up the build.
  optional?: boolean;
}

export const STEPS: StepDef[] = [
  { key: 'add_client', label: 'Add client', plain: 'Your client workspace', kind: 'working' },
  // `brand_kit` (was `look`): the design system to approve — no website yet.
  { key: 'brand_kit', label: 'Brand kit', plain: 'Lock the fonts, colours & grid', kind: 'working' },
  // `assets` (assets-step.md): the client's static site-chrome files (SVG/PNG logos, maps,
  // graphics) → sites/<slug>/public/assets/, referenced by the build agent at /assets/<name>.
  // Distinct from Brand kit (tokens) and Content & media (portfolio → CF Images/Stream). Optional
  // (never gates Build; a client with no odd assets just skips it).
  { key: 'assets', label: 'Assets', plain: 'Add logos, maps & graphics the site uses', kind: 'working', optional: true },
  // `content_media`: drop the client's real photos/video — skippable.
  { key: 'content_media', label: 'Content & media', plain: 'Add the client’s photos & video', kind: 'working', optional: true },
  // `site_structure` (IA-2): the page tree — pages · menu (page→nav) · footer. isDone is
  // "effectively free" (Home always exists → ≥1 page) so it never HARD-gates SEO/Build; but
  // the LANDING function does NOT treat it always-done (see landingStepKey), so a fresh
  // branded client lands here first — Structure → SEO → Build, the intended arc.
  { key: 'site_structure', label: 'Site structure', plain: 'Set the pages, menu & footer', kind: 'working' },
  // SEO before Build (IA-2): the SEO chat reasons off the page tree (no built routes needed),
  // finalizes the handoff doc + site-wide outputs, then each page self-SEOs during its build.
  { key: 'seo', label: 'SEO', plain: 'Get found on Google', kind: 'working', optional: true },
  // key stays 'review' (wizard_state + gating depend on it); the LABEL is "Build" —
  // the "Review" name was a leftover from the old one-shot form, it's the build chat now.
  { key: 'review', label: 'Build', plain: 'Built in the hand-build session', kind: 'working' },
  { key: 'preview_approve', label: 'Preview & approve', plain: 'Client signs off the preview', kind: 'working' },
  // key stays `send_live` (stable; build history + persisted state reference it); label is "Go live".
  { key: 'send_live', label: 'Go live', plain: 'Publish the site', kind: 'working' },
  { key: 'handover', label: 'Handover', plain: 'Wrap up & hand off', kind: 'working' },
];

export interface WizardState {
  step?: string;
  steps?: Record<string, 'done'>;
  handover_done?: boolean;
  // The build chat records each section under wizard_state.sections; we read it
  // (build-independent) so landing/gating is correct the instant a client opens.
  sections?: Record<string, { locked?: boolean }>;
  // Autosaved, in-progress form values per step (survives navigate-away/refresh).
  // brandArtboards: reference images uploaded in the brand kit, fed to extraction.
  // brandTokens (#GR3): the in-progress brand-kit edits (font pick, colours, scale,
  // grid…) before Lock — so a navigate-away/remount doesn't reset them to the committed
  // tokens. Cleared/ignored once brand_kit is locked (the committed tokens win then).
  drafts?: { brandArtboards?: string[]; brandTokens?: TokenSchema };
  // Responsive checklist ticks (responsive-checklist.md) — per viewport id: the viewport pass
  // flag + the per-CHECK sub-ticks. Advisory, never gates go-live; whole-site (not per-page) in MVP.
  responsive?: Record<string, { checked?: boolean; checks?: Record<string, boolean> }>;
  // go-live-protection §5 (Decision C): the client's sign-off on the preview. Recorded when the
  // designer clicks approve; `sha` pins the preview's deploy commit it was given for, so a later
  // deploy (a material change) makes it stale → it's cleared and must be re-done. Go-live
  // (Decision B / go-live.ts) REQUIRES this. No migration — a wizard_state JSON key. `sha` may be
  // null (approved before the deploy row caught up → never goes stale; the explicit flag is the floor).
  // null = an explicitly CLEARED sign-off (a stale approval dropped on a material preview change).
  // The wizard-state merge is presence-gated, so clearing must send null, not omit the key.
  client_approved?: { at: string; sha: string | null } | null;
  // Go-live checklist ticks (go-live-checklist.md §B/§C) — per manual itemId: 'done' | 'na'
  // ('open' = key absent). Items 1 (responsive) + 9 (client approved) are derived roll-ups, not
  // stored here. Mirrors `responsive`; MUST be in WIZARD_OWNED_KEYS (wizard-state.ts) or the merge
  // guard strips it on save (the #373 lesson). See src/lib/goliveChecklist.ts (GoLiveMarks).
  golive?: Record<string, 'done' | 'na'>;
}

/**
 * The landing step for a client, computed from its PERSISTED wizard_state only —
 * deliberately build-independent, so selecting a client lands on its own first incomplete
 * step immediately (builds refetch async and would be stale here, which previously leaked
 * the prior client's frontier — e.g. a new client opening on Handover). Required steps gate;
 * optional steps (Content & media, SEO) are skipped over.
 */
export function landingStepKey(ws: WizardState): string {
  const doneFromWs = (key: string): boolean => {
    if (key === 'add_client') return true;
    if (key === 'brand_kit') return ws.steps?.brand_kit === 'done' || ws.steps?.look === 'done';
    // IA-2 (① review): NOT always-done for LANDING (unlike isDone, which keeps it free so
    // Build is never hard-gated + SEO stays optional). A fresh branded client should LAND on
    // Site structure → Continue → SEO → Build (the intended arc), not skip straight to Build
    // (which would build with no SEO doc → pages can't self-SEO). The reviewComplete guard
    // stops an already-built client being bounced back to Structure.
    if (key === 'site_structure')
      return ws.steps?.site_structure === 'done' || reviewComplete(ws as Record<string, unknown>);
    if (key === 'review') {
      // M-P2: whole-site gate (pages layer) with a legacy single-page fallback.
      return reviewComplete(ws as Record<string, unknown>) || ws.steps?.review === 'done';
    }
    if (key === 'send_live') return ws.steps?.send_live === 'done' || !!ws.handover_done;
    if (key === 'handover') return !!ws.handover_done;
    return ws.steps?.[key] === 'done';
  };
  const i = STEPS.findIndex((s) => s.kind === 'working' && !s.optional && !doneFromWs(s.key));
  return (i === -1 ? STEPS[STEPS.length - 1] : STEPS[i]).key;
}
